import { useCallback, useEffect, useRef, useState } from 'react';

/** Long enough to finish a word, short enough not to feel laggy. */
export const SEARCH_DEBOUNCE_MS = 300;

/**
 * A search input whose committed value lives in the URL.
 *
 * The input has to stay responsive per keystroke while the URL — and the
 * request behind it — only changes once typing pauses, and it has to follow the
 * URL when the *user* changes it (back button, a shared link, a cleared
 * filter). Doing both naively fights itself: an effect that mirrors the URL into
 * the field will overwrite the character typed while the last commit was in
 * flight.
 *
 * The fix is to remember what this hook last committed. A URL value that
 * matches it is this hook's own echo and is ignored; anything else came from
 * outside and wins.
 */
export const useDebouncedSearch = (
  urlValue: string,
  commit: (value: string) => void,
  delay: number = SEARCH_DEBOUNCE_MS,
): readonly [string, (value: string) => void, () => void] => {
  const [draft, setDraft] = useState(urlValue);
  const committedRef = useRef(urlValue);

  useEffect(() => {
    if (urlValue !== committedRef.current) {
      committedRef.current = urlValue;
      setDraft(urlValue);
    }
  }, [urlValue]);

  useEffect(() => {
    if (draft === committedRef.current) {
      return undefined;
    }

    const timer = globalThis.setTimeout(() => {
      committedRef.current = draft;
      commit(draft);
    }, delay);

    return () => {
      globalThis.clearTimeout(timer);
    };
  }, [draft, delay, commit]);

  /** Clear the field and the URL together, with no debounce in between. */
  const clear = useCallback(() => {
    committedRef.current = '';
    setDraft('');
    commit('');
  }, [commit]);

  return [draft, setDraft, clear] as const;
};
